import React, { useState, useEffect } from "react";
import Select, { components as SelectComponents, IndicatorProps, OptionProps as InnerOptionProps } from "react-select";
import classNames from "classnames";
import voca from "voca";

import { LoadingSkeleton } from "../../LoadingSkeleton";
import ReadOnlyContainer from "../../ReadOnlyContainer";

import setHelperText from "../helpers/setHelperText";
import { WrappedFieldInputProps, WrappedFieldMetaProps } from "redux-form";
import { ContainerProps } from "react-select";

export type SelectValueType = string | number | boolean;

export interface LabelType {
  label: string;
  value: string | number | boolean;
}

export interface SelectFieldProps {
  input?: Partial<WrappedFieldInputProps>;
  helperText?: string;
  helperTextEnabled?: boolean;
  disabled?: boolean;
  label?: string;
  id?: string;
  // this is truly the only mandatory field
  options: Array<{ label: string; value: SelectValueType }>;
  meta?: Partial<WrappedFieldMetaProps>;
  placeholder?: string;
  showRequiredStar?: boolean;
  readOnly?: boolean;
  isMulti?: boolean;
  isClearable?: boolean;
  extraOptionsClass?: string;
  hyperLink?: string;
  hyperLinkText?: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DropdownIndicator: React.FC<IndicatorProps<any, any>> = (props: IndicatorProps<any, any>): JSX.Element => {
  return (
    <SelectComponents.DropdownIndicator {...props}>
      <>
        <div data-testid="test__open_select" className="caprx-icon-inputarrowcaretdown caprx-f-elem-select-icons"></div>
        <div data-testid="test__close_select" className="caprx-icon-inputarrowcaretup caprx-f-elem-select-icons"></div>
      </>
    </SelectComponents.DropdownIndicator>
  );
};

interface SelectProps {
  ["data-testid"]: string;
  id: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface SelectContainerProps extends ContainerProps<any, any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  innerProps: any;
  selectProps: SelectProps;
}

const SelectContainer: React.FC<SelectContainerProps> = (props: SelectContainerProps): JSX.Element => {
  return (

    <SelectComponents.SelectContainer
      {...props}
      innerProps={Object.assign({}, props.innerProps, {
        "data-testid": props.selectProps["data-testid"],
      })}
    />
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface OptionProps extends InnerOptionProps<any, any> {
  data: {
    test_id: string;
    label: string;
    value: string | number;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  innerProps: any;
  selectProps: SelectProps;
}

const Option = (props: OptionProps): JSX.Element => {
  let {
    data: { test_id = null },
  } = props;

  if (test_id === null) {
    const {
      data: { label },
    } = props;
    test_id = voca.snakeCase(label);
  }

  const {
    selectProps: { id = "select" },
  } = props;
  const {
    selectProps: { "data-testid": parentTestId = `test__${id}` },
  } = props;
  const { innerProps } = props;

  const testId = `${parentTestId}_${test_id}`.toLowerCase();

  innerProps["data-testid"] = testId;
  const className = classNames(
    props.className,
    "option",
    "content",
    {
      focused: props.isFocused,
    },
  );

  return <SelectComponents.Option {...props} className={className} innerProps={innerProps} />;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MenuList = (props: any): JSX.Element => {
  return <SelectComponents.MenuList {...props} className={classNames("items", { "opened": props.selectProps.menuIsOpen }, props.className)} />;
};

export const SelectField: React.FC<SelectFieldProps> = (props: SelectFieldProps): JSX.Element => {
  const {
    input,
    helperText,
    helperTextEnabled = true,
    disabled,
    label,
    id,
    meta: { touched, error },
    placeholder,
    showRequiredStar = false,
    readOnly = false,
    isMulti = false,
    isClearable = true,
    extraOptionsClass,
    hyperLink,
    hyperLinkText,
  } = props;
  let options = props.options;
  const [animate, setAnimate] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedValue, setSelectedValue] = useState<any>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleOnChange = (data: any, triggeredAction?: any): any => {
    // if data is falsey and it is not of type number
    if (!data && typeof data !== "number" && typeof data !== "boolean") {
      input.onChange(data, triggeredAction);
      setSelectedValue(data);
      return null;
    }

    if (isMulti) {
      setSelectedValue(data);
      input.onChange(data, triggeredAction);

      if (data.length === 0) {
        input.onChange(null, triggeredAction);
      }
      return null;
    }

    /* if data.value is truthy, or if an empty string is passed in use it to clear the field,
       or if data.value is a number */
    if (data.value || data.value === "" || typeof data.value === "number" || typeof data.value === "boolean") {
      input.onChange(data.value, triggeredAction);
      setSelectedValue(data);
    } else {
      setSelectedValue(options.find((option) => option.value === data));
    }
  };

  useEffect(() => {
    // (input.value === "" && readOnly) allows values to be cleared when the form is marked as disabled
    if ((typeof input.value !== "string" || !selectedValue) && (!readOnly || (input.value === "" && readOnly))) {
      if (
        Object.keys(input.value).length !== 0
        || (typeof input.value === "number" && input.value !== null && input.value !== undefined)
        || (typeof input.value === "boolean" && input.value !== null && input.value !== undefined)
      ) {
        handleOnChange(input.value);
        setAnimate(true);
      }
    }

    if (typeof input.value === "string" && selectedValue !== null) {
      handleOnChange(input.value);
      setAnimate(true);
    }

    /* This was made strictly for our plan benefit version conditional select fields.
    When clearing a parent select field w/ dispatch. The subsequent input values become empty strings.
    However, selectedValue never gets cleared at that time. If still lingering, reset it. */
    if (input.value === "" && selectedValue !== null) {
      handleOnChange(null);
      setAnimate(false);
    }
  }, [input]);

  const handleAnimation = (): void => {
    // if input.value is truthy or it is of the type number
    if (input.value || typeof input.value === "number" || selectedValue) {
      setAnimate(true);
    } else {
      setAnimate(false);
    }
  };

  const handleOnBlur = (): void => {
    if (input.onBlur) input.onBlur(undefined);
    handleAnimation();
  };

  const formElemRootClasses = classNames(
    "caprx-f-elem-root",
    "caprx-f-elem-select-wrapper",
    { "caprx-f-elem-error": touched && error },
    { "caprx-f-elem-disabled": disabled },
    { "caprx-f-elem-animate": readOnly },
  );

  const selectClasses = classNames("caprx-f-elem-select", { "caprx-f-elem-select-animate": animate }, extraOptionsClass);

  if (!Array.isArray(options)) {
    options = [];
  }

  // Making this any fixes an annoying build error in latest TS... these types are all kinds of wrong, but really not going to figure it out now.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const components: any = {
    DropdownIndicator,
    SelectContainer,
    Option,
    MenuList,
  };

  let body = (
    <Select
      {...props}
      id={id}
      options={options}
      className={selectClasses}
      classNamePrefix="caprx-f-elem-select-child"
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      onFocus={(): void => setAnimate(true)}
      value={selectedValue}
      openMenuOnFocus
      tabSelectsValue={false}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={disabled}
      autosize={false}
      components={components}
      showRequiredStar={showRequiredStar}
    />
  );

  if (readOnly) {
    if (input.value === "" || input.value === undefined) {
      input.value = "N/A";
    }

    let readOnlyValue = input.value;

    if (Array.isArray(input.value) && input.value.length > 0) {
      const arrayReadOnlyValues: Array<string> = [];

      input.value.forEach((option: LabelType) => {
        const displayOptions = options.find((obj) => obj.value === option.value);
        if (displayOptions) {
          arrayReadOnlyValues.push(displayOptions.label);
        }
      });

      readOnlyValue = arrayReadOnlyValues.join(", ");
    } else {
      const displayOptions = options.find((obj) => obj.value === input.value);
      if (displayOptions) {
        readOnlyValue = displayOptions.label;
      }
    }

    body = <ReadOnlyContainer id={id} body={readOnlyValue} hyperLink={hyperLink} hyperLinkText={hyperLinkText} />;
  }

  return (
    <LoadingSkeleton>
      <div className={formElemRootClasses}>
        {body}
        <label htmlFor={id} className="caprx-f-elem-label">
          {label}
          {showRequiredStar && <span className="required-star">*</span>}
        </label>
        {helperTextEnabled && <div className="caprx-f-elem-helper-text">{setHelperText(helperText, touched, error)}</div>}
      </div>
    </LoadingSkeleton>
  );
};

export default SelectField;
